import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Shield, Sparkles } from 'lucide-react';

const TierAnalysisLoader = ({ tier = 'reach' }) => {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('opacity-100');

  // Strategic insights by tier - following UX Philosophy: "Make loading states so valuable that students don't mind the wait"
  const strategicInsights = {
    reach: [
      {
        icon: <Sparkles className="w-5 h-5" />,
        insight: "73% of students admitted to reach schools had unique extracurriculars, not just high grades",
        value: "Your spike matters more than well-roundedness at highly selective schools"
      },
      {
        icon: <TrendingUp className="w-5 h-5" />,
        insight: "Applying EA/ED to reach schools can increase admission chances by 10-15%",
        value: "Strategic timing is as important as qualifications"
      },
      {
        icon: <Target className="w-5 h-5" />,
        insight: "Reach schools often have higher acceptance rates for students from underrepresented states",
        value: "Geographic diversity can be a strategic advantage"
      },
      {
        icon: <Sparkles className="w-5 h-5" />,
        insight: "Lesser-known insight: Reach school acceptance rates vary by major—some programs are 2x easier to get into",
        value: "Strategic major selection can improve your chances"
      }
    ],
    target: [
      {
        icon: <Target className="w-5 h-5" />,
        insight: "Target schools are where you're most likely to get merit aid—average $15K-25K per year",
        value: "Financial strategy: Target schools often offer the best value"
      },
      {
        icon: <Sparkles className="w-5 h-5" />,
        insight: "Hidden opportunity: Target schools often have honors colleges that rival reach school experiences",
        value: "Look beyond rankings—honors programs provide elite resources"
      },
      {
        icon: <TrendingUp className="w-5 h-5" />,
        insight: "Application tip: Target schools value demonstrated interest—visit or attend virtual events",
        value: "Showing genuine interest can move you from maybe to yes"
      },
      {
        icon: <Target className="w-5 h-5" />,
        insight: "Smart positioning: Being in the top 25% of admitted students at target schools opens more opportunities",
        value: "Scholarships, research positions, and leadership roles favor top performers"
      }
    ],
    safety: [
      {
        icon: <Shield className="w-5 h-5" />,
        insight: "Your safety schools should excite you, not just be backups—look for unique programs",
        value: "Many students end up happiest at their safety schools"
      },
      {
        icon: <Sparkles className="w-5 h-5" />,
        insight: "Financial advantage: Safety schools often offer the best merit aid packages to attract top students",
        value: "Being a star applicant = significant financial benefits"
      },
      {
        icon: <TrendingUp className="w-5 h-5" />,
        insight: "Career outcome: Many safety schools have better industry connections than higher-ranked alternatives",
        value: "Job placement matters more than brand name in many fields"
      },
      {
        icon: <Shield className="w-5 h-5" />,
        insight: "Strategic insight: Safety schools with rolling admissions provide early confidence boosts",
        value: "Early acceptances reduce stress during the rest of application season"
      }
    ]
  };

  const insights = strategicInsights[tier] || strategicInsights.reach;

  // Rotate through insights every 4 seconds with fade animation
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeClass('opacity-0');
      setTimeout(() => {
        setCurrentTipIndex((prev) => (prev + 1) % insights.length);
        setFadeClass('opacity-100');
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, [insights.length]);

  const currentInsight = insights[currentTipIndex];

  // Tier-specific colors
  const tierColors = {
    reach: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
    target: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    safety: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' }
  };

  const colors = tierColors[tier] || tierColors.reach;

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6">
      {/* Main Loader */}
      <div className="relative">
        <div className={`absolute inset-0 ${colors.bg} rounded-full blur-xl opacity-30 animate-pulse`} />
        <div className={`w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin relative z-10`} />
      </div>

      {/* Stage Indicator */}
      <div className="text-center space-y-2">
        <h3 className="text-sm font-bold text-slate-900">
          Analyzing {tier.charAt(0).toUpperCase() + tier.slice(1)} Tier
        </h3>
        <p className="text-xs text-slate-600 animate-pulse">
          Evaluating balance, confidence, and strategic opportunities
        </p>
      </div>

      {/* Strategic Insight Carousel - following UX Philosophy */}
      <div className={`transition-opacity duration-300 ${fadeClass} max-w-md`}>
        <div className={`${colors.bg} rounded-xl p-6 border-2 ${colors.border} shadow-sm`}>
          <div className="flex items-start space-x-3">
            <div className={`flex-shrink-0 ${colors.text} mt-0.5`}>
              {currentInsight.icon}
            </div>
            <div className="flex-1 space-y-2">
              <p className="font-semibold text-slate-900 text-xs leading-snug">
                💡 {currentInsight.insight}
              </p>
              <p className={`text-xs ${colors.text} leading-relaxed font-medium`}>
                {currentInsight.value}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Dots */}
      <div className="flex space-x-1.5">
        {insights.map((_, index) => (
          <div
            key={index}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === currentTipIndex
                ? 'w-8 bg-slate-900'
                : 'w-1.5 bg-slate-300'
            }`}
          />
        ))}
      </div>

      {/* Time Estimate */}
      <p className="text-xs text-slate-500 mt-4">
        Typically takes 10-20 seconds for comprehensive tier analysis
      </p>
    </div>
  );
};

export default TierAnalysisLoader;
